/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { AnyItem } from '../../registry/ComponentRegistry';
import { getDataType } from '../../actions/ActionSetQueryFilter/ActionSetQueryFilterManager';
import { IconType } from '../../icons/types';
import { designPath } from '../../../designer/atoms';
import { Platform } from '../../platform/Platform';
import {
  AliasType,
  ComponentManager,
  ComponentManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultOperators, SmartSearchItem } from '../SmartSearch';
import { defaultValue, SmartSearchAttributeItem } from './SmartSearchAttribute';

const SmartSearchAttributeManager: ComponentManagerFn<
  'SmartSearchAttribute'
> = (platform: Platform): ComponentManager<'SmartSearchAttribute'> => {
  let callback1: () => void = emptyFunction;
  let callback2: () => void = emptyFunction;
  let operatorCallback = emptyFunction;
  let optionsCallback = emptyFunction;

  return {
    ...baseComponentManager<'SmartSearchAttribute'>(
      platform,
      'SmartSearchAttribute',
    ),
    isChildAllowed: (page, item, childType) => false,
    defaultValue,
    defaultChildren: [],
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'LOV' ? { aliasProp: 'dsAlias' } : undefined,
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'baseDataType':
          return false;
        case 'viewAttrCode':
        case 'displayAttr':
          return (
            itemData.dataType === 'Select' &&
            !itemData.options &&
            !!itemData.dsAlias
          );
        case 'dsAlias':
          return itemData.dataType === 'Select' && !itemData.options;
        case 'options':
          return itemData.dataType === 'Select';
        case 'allowDecimal':
        case 'allowNegative':
          return (
            itemData.baseDataType === 'Number' && itemData.dataType !== 'Select'
          );
        case 'dataType':
          return (
            itemData.baseDataType === 'Text' ||
            itemData.baseDataType === 'Number' ||
            itemData.baseDataType === ''
          );
        case 'showTime':
          return (
            itemData.baseDataType === 'DateTime' ||
            itemData.baseDataType === 'StringDateTime'
          );
        case 'lookup':
          return itemData.dataType === 'Lookup';
        default:
          return true;
      }
    },
    props: {
      attributeCode: {
        label: 'Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => 'ALL',
        getAlias: (page, item, { appUid, pageId, itemId }) => {
          const parent = page[item.parent] as SmartSearchItem;
          const targetId =
            parent?.props.gridId || parent?.props.pivotId || itemId;
          const alias =
            platform.getDataSourceAlias(appUid, pageId, targetId, 'STORE')
              ?.alias || '';
          return {
            alias,
            embeddedPageId: pageId,
          };
        },
        preventMultiEdit: true,
      },
      label: {
        label: 'Label',
        type: 'string',
      },
      baseDataType: {
        label: 'Base Data Type: Not Visible',
        type: 'string',
      },
      dataType: {
        label: 'Control Type',
        type: 'select',
        required: true,
        useDropDown: false,
        options: async (
          item: RequiredItem<'SmartSearchAttribute'>,
          { appUid, pageId, itemId },
        ): Promise<Record<string | number, string | number | boolean>> => {
          let baseDataType = item.props.baseDataType;
          if (!baseDataType) {
            baseDataType = (
              await getDataType({
                appUid,
                pageId,
                itemId,
                attribute: item.props.attributeCode,
                itemType: 'SmartSearchAttribute',
                platform,
                storeType: 'STORE',
              })
            )[0];
            platform.setter(
              designPath({
                appUid,
                pageId,
                itemId,
                key: 'props',
                subkey: 'baseDataType',
              }),
              baseDataType,
            );
          }
          if (baseDataType === 'Number') {
            return {
              Number: 'Number',
              Select: 'Select',
              Lookup: 'Lookup',
            };
          }
          return {
            Text: 'Text',
            Select: 'Select',
            Lookup: 'Lookup',
          };
        },
        optionsRefreshCallback: (cb) => {
          optionsCallback = cb;
        },
      },
      defaultOperator: {
        label: 'Default Operator',
        type: 'operator',
        required: true,
        refreshCallback: (cb: () => void) => {
          operatorCallback = cb;
        },
        getDataType: async (item: RequiredItem<'SmartSearchAttribute'>) => {
          return item.props.dataType;
        },
      },
      excludeOperators: {
        label: 'Exclude Operators',
        type: 'multiSelectOperator',
        refreshCallback: (cb: () => void) => {
          operatorCallback = cb;
        },
        getDataType: async (item: RequiredItem<'SmartSearchAttribute'>) => {
          return item.props.dataType;
        },
        helpText:
          'Choose one or more operators that should be excluded from appearing in the SmartSearch filter dropdown.',
      },
      lookup: {
        label: 'Lookup',
        type: 'LookupCodeChooser',
      },
      options: {
        label: 'Simple Values',
        type: 'string',
        helpText: 'e.g CA:California, TX:Texas',
      },
      optionsWidth: {
        label: 'Options Width',
        type: 'number',
        category: 'Select Options',
        min: 0,
        max: 1000,
        step: 50,
      },
      dsAlias: {
        label: 'Data Source Alias',
        type: 'DataSourceAliasChooser',
        allowedTypes: ['CloudDataStore', 'ReadOnlyDataStore'],
      },
      viewAttrCode: {
        label: 'Value Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'LOV',
        getAttributeDataTypes: (item: RequiredItem<'SmartSearchAttribute'>) => {
          switch (item.props.baseDataType) {
            case 'Boolean':
              return ['Boolean'];
            case 'Text':
              return ['String', 'Textarea'];
            case 'Number':
              return ['Integer', 'Double', 'Decimal'];
            case 'YN':
              return ['YN'];
            case 'Date':
              return ['Date', 'DateTime'];
            default:
              return 'ALL';
          }
        },
        preventMultiEdit: true,
        attributeRefreshCallback: (cb) => {
          callback1 = cb;
        },
      },
      displayAttr: {
        label: 'Display Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'LOV',
        getAttributeDataTypes: () => ['String', 'Textarea', 'Integer'],
        preventMultiEdit: true,
        attributeRefreshCallback: (cb) => {
          callback2 = cb;
        },
      },
      allowDecimal: {
        label: 'Allow Decimal',
        type: 'boolean',
      },
      allowNegative: {
        label: 'Allow Negative',
        type: 'boolean',
      },
      showTime: {
        label: 'Show Time',
        type: 'boolean',
      },
    },
    onPropertyChange: (
      property,
      value,
      itemProps,
      { appUid, pageId, itemId },
    ) => {
      if (property === 'dsAlias') {
        callback1();
        callback2();
        return {
          [property]: value,
          options: defaultValue.options,
          lookup: defaultValue.lookup,
        };
      }
      if (property === 'options') {
        return {
          [property]: value,
          dsAlias: defaultValue.dsAlias,
          viewAttrCode: defaultValue.viewAttrCode,
          displayAttr: defaultValue.displayAttr,
          lookup: defaultValue.lookup,
        };
      }
      if (property === 'dataType') {
        operatorCallback();
        return {
          [property]: value,
          defaultOperator: defaultValue.defaultOperator,
          options: defaultValue.options,
          dsAlias: defaultValue.dsAlias,
          viewAttrCode: defaultValue.viewAttrCode,
          displayAttr: defaultValue.displayAttr,
          allowDecimal: defaultValue.allowDecimal,
          allowNegative: defaultValue.allowNegative,
          lookup: defaultValue.lookup,
        };
      }

      if (property === 'attributeCode') {
        if (typeof value === 'string') {
          getDataType({
            appUid,
            pageId,
            itemId,
            attribute: value,
            itemType: 'SmartSearchAttribute',
            platform,
            storeType: 'STORE',
          }).then(([dataType, attr, baseDataType]) => {
            platform.setter(
              designPath({
                appUid,
                pageId,
                itemId,
                key: 'props',
                subkey: 'baseDataType',
              }),
              baseDataType,
            );
            platform.setter(
              designPath({
                appUid,
                pageId,
                itemId,
                key: 'props',
                subkey: 'dataType',
              }),
              dataType,
            );
            platform.setter(
              designPath({
                appUid,
                pageId,
                itemId,
                key: 'props',
                subkey: 'defaultOperator',
              }),
              defaultOperators[dataType],
            );
            if (attr) {
              platform.setter(
                designPath({
                  appUid,
                  pageId,
                  itemId,
                  key: 'props',
                  subkey: 'label',
                }),
                attr.attributeName,
              );
            }
            operatorCallback();
            optionsCallback();
          });
        }
        return {
          [property]: value,
          dataType: defaultValue.dataType,
          defaultOperator: defaultValue.defaultOperator,
          options: defaultValue.options,
          dsAlias: defaultValue.dsAlias,
          viewAttrCode: defaultValue.viewAttrCode,
          displayAttr: defaultValue.displayAttr,
          allowDecimal: defaultValue.allowDecimal,
          allowNegative: defaultValue.allowNegative,
          label: defaultValue.label,
          lookup: defaultValue.lookup,
        };
      }
      return {
        [property]: value,
      };
    },
    name: 'Search Attribute',
    isDataSourceAliasUsed: (item, alias) => item.props.dsAlias === alias,
    getDisplayText: (item) =>
      `${item.props.label}${
        item.props.dsAlias && !item.props.options
          ? ` [${item.props.dsAlias}]`
          : item.props.lookup.code
          ? ` [${item.props.lookup.code}]`
          : ''
      }`,
    canHaveChildren: () => false,
    category: 'Data',
    layout: 'sizeAware',
    icon: (item: AnyItem) => {
      const dataType = (item as SmartSearchAttributeItem).props.dataType;
      let iconName = 'io-text';
      let iconType: IconType = 'cloudio';
      if (dataType === 'Number') {
        iconName = 'io-int-attr';
      } else if (dataType === 'Date') {
        iconName = 'calendar-alt';
        iconType = 'light';
      } else if (dataType === 'Boolean' || dataType === 'YN') {
        iconName = 'check-square';
        iconType = 'light';
      } else if (dataType === 'Select' || dataType === 'Lookup') {
        iconName = 'io-select';
      }
      return { iconName, iconType };
    },
    description: `**Overview**<br/>
The Smart Search Attribute defines a single queryable dimension within a Smart Search component.
While the parent Smart Search handles the query logic and UI, this component tells the system what can be searched (e.g., "Order Date," "Status," "Total Amount") and how the user interacts with it (e.g., via a Text Box, Date Picker, or Dropdown).

**1. Attribute Binding & Type Resolution**<br/>
The core of this component is mapping a UI label to a backend data field.
* **Attribute:** Select the data field from the store (e.g., "status").
* **Base Data Type:** The system automatically introspects the backend schema to determine the underlying type (Text, Number, Date, Boolean). This is read-only but informs the available configuration options.
* **Control Type (Data Type):** Determines the UI widget used for filtering.
    * **Text:** Standard input box (Contains, Equals).
    * **Number:** Numeric input (Greater Than, Less Than).
    * **Date:** Calendar picker (Before, After, Between).
    * **Select / Lookup:** Dropdown menu (One Of, Not One Of).

**2. Value Sources (For Select/Lookup Types)**<br/>
If you choose Select or Lookup as the Control Type, you must define where the dropdown options come from. You have three mutually exclusive choices:
* **Simple Values:** A static, comma-separated list. Format: Value:Label (e.g., "A:Active, I:Inactive, P:Pending"). Best for small, unchanging lists.
* **Lookup Code:** Binds to a centralized System Lookup (LOV). Best for shared reference data like "Country Codes" or "Currency."
* **Data Source Alias:** Binds to another Data Store. You must select the Alias, the Value Attribute (what is stored), and the Display Attribute (what the user sees). Best for dynamic lists like "Assigned To User" or "Product Category."

**3. Operator Logic**<br/>
You can restrict how users are allowed to query this specific field.
* **Default Operator:** The operator pre-selected when the user adds this filter (e.g., set "Status" to default to Equals).
* **Exclude Operators:** A multi-select list to hide specific operators. (Use Case: For a "Description" field, you might want to exclude Equals and only allow Contains to prevent users from trying to match exact paragraphs).

**4. Formatting & Constraints**<br/>
* **Label:** The human-readable name shown in the dropdown (e.g., rename "emp_fname" to "First Name").
* **Numeric Settings:** Options to Allow Decimal or Allow Negative values.
* **Date Settings:** Toggle Show Time to include a time selector in the date picker.

**5. Integration**<br/>
* **Parent Requirement:** This component allows no placement flexibility. It must be a direct child of a **Smart Search** component.`,
  };
};

export default SmartSearchAttributeManager;
